import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  FiSearch, FiPlus, FiTrash2, FiEdit2, FiChevronDown, FiChevronRight,
  FiCheck, FiX, FiSave, FiUserPlus, FiList, FiArrowLeft,
  FiRefreshCw, FiExternalLink, FiAward, FiEye, FiEyeOff
} from 'react-icons/fi';
import {
  lmsSearchStudents, lmsGetStudentEnrollments, lmsEnrollStudent,
  lmsGetEnrollment, lmsUpdateEnrollmentInfo,
  lmsAddModule, lmsUpdateModule, lmsDeleteModule,
  lmsAddItem, lmsUpdateItem, lmsDeleteItem,
  lmsGradeAssignment, lmsGetAllEnrollments, getAdminCourses
} from '../utils/api';

/* ─── Type config ─── */
const TYPES = {
  video:      { emoji: '▶️', label: 'Video',      bg: '#dbeafe', color: '#1d4ed8' },
  file:       { emoji: '📄', label: 'File',       bg: '#f3e8ff', color: '#7c3aed' },
  assignment: { emoji: '📝', label: 'Assignment', bg: '#fef3c7', color: '#b45309' },
  note:       { emoji: '📌', label: 'Note',       bg: '#fee2e2', color: '#dc2626' },
  link:       { emoji: '🔗', label: 'Link',       bg: '#d1fae5', color: '#059669' },
};

const BLANK_ITEM = {
  type: 'video', title: '', description: '',
  videoUrl: '', videoDuration: '',
  fileUrl: '', fileName: '', fileSize: '', fileType: '',
  linkUrl: '', dueDate: '', maxMarks: '', isVisible: true,
};

/* ─── Helpers ─── */
const Spin = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
    <div className="spinner" />
  </div>
);

const TypeChip = ({ type }) => {
  const t = TYPES[type] || TYPES.file;
  return (
    <span style={{
      background: t.bg, color: t.color, fontSize: 12, fontWeight: 700,
      padding: '3px 10px', borderRadius: 999,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {t.emoji} {t.label}
    </span>
  );
};

/* ─── Item Form ─── */
const ItemForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState({ ...BLANK_ITEM, ...(initial || {}) });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  return (
    <div style={{
      background: '#f0f4ff', border: '2px dashed var(--primary)',
      borderRadius: 12, padding: 20, marginTop: 8,
    }}>
      <div className="form-grid" style={{ marginBottom: 12 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Type</label>
          <select value={f.type} onChange={e => set('type', e.target.value)} className="form-control">
            {Object.entries(TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.emoji} {v.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Title <span style={{ color: 'red' }}>*</span></label>
          <input value={f.title} onChange={e => set('title', e.target.value)}
            className="form-control" placeholder="e.g. Intro to React" autoFocus />
        </div>
      </div>

      <div className="form-group">
        <label>Description / Instructions</label>
        <textarea value={f.description} onChange={e => set('description', e.target.value)}
          className="form-control" rows={2}
          placeholder="Optional notes for the student…"
          style={{ resize: 'vertical' }} />
      </div>

      {f.type === 'video' && (
        <div className="form-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Video URL <span style={{ color: 'var(--mid)', fontSize: 11 }}>(YouTube / Drive / Vimeo)</span></label>
            <input value={f.videoUrl} onChange={e => set('videoUrl', e.target.value)}
              className="form-control" placeholder="https://youtube.com/watch?v=…" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Duration</label>
            <input value={f.videoDuration} onChange={e => set('videoDuration', e.target.value)}
              className="form-control" placeholder="14:32" />
          </div>
        </div>
      )}

      {f.type === 'file' && (
        <div className="form-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File URL</label>
            <input value={f.fileUrl} onChange={e => set('fileUrl', e.target.value)}
              className="form-control" placeholder="https://drive.google.com/…" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File Name</label>
            <input value={f.fileName} onChange={e => set('fileName', e.target.value)}
              className="form-control" placeholder="Notes_Week1.pdf" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File Type</label>
            <input value={f.fileType} onChange={e => set('fileType', e.target.value)}
              className="form-control" placeholder="pdf / docx / zip" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>File Size</label>
            <input value={f.fileSize} onChange={e => set('fileSize', e.target.value)}
              className="form-control" placeholder="2.4 MB" />
          </div>
        </div>
      )}

      {f.type === 'link' && (
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>URL</label>
          <input value={f.linkUrl} onChange={e => set('linkUrl', e.target.value)}
            className="form-control" placeholder="https://…" />
        </div>
      )}

      {f.type === 'assignment' && (
        <div className="form-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Due Date</label>
            <input type="date" value={f.dueDate}
              onChange={e => set('dueDate', e.target.value)} className="form-control" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Max Marks</label>
            <input type="number" value={f.maxMarks}
              onChange={e => set('maxMarks', e.target.value)}
              className="form-control" placeholder="100" />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
          <input type="checkbox" checked={!!f.isVisible}
            onChange={e => set('isVisible', e.target.checked)} />
          Visible to student now
        </label>
        <div style={{ flex: 1 }} />
        <button onClick={onCancel} className="btn btn-outline btn-sm">
          <FiX size={13} /> Cancel
        </button>
        <button onClick={() => onSave(f)} disabled={saving || !f.title.trim()} className="btn btn-primary btn-sm">
          {saving ? 'Saving…' : <><FiSave size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
};

/* ─── Grade Modal ─── */
const GradeModal = ({ item, moduleId, enrollmentId, onClose, onDone }) => {
  const [marks,    setMarks]    = useState(item.marksObtained ?? '');
  const [feedback, setFeedback] = useState(item.feedback ?? '');
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    if (marks === '') { toast.error('Enter marks'); return; }
    setSaving(true);
    try {
      await lmsGradeAssignment(enrollmentId, moduleId, item._id, {
        marksObtained: Number(marks), feedback,
      });
      toast.success('Graded! ✅');
      onDone();
      onClose();
    } catch { toast.error('Grade save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>Grade Assignment</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>
            <FiX />
          </button>
        </div>
        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <b>{item.title}</b>
          <div style={{ fontSize: 13, color: 'var(--mid)' }}>Max Marks: {item.maxMarks || '—'}</div>
        </div>
        {item.submissionUrl && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #6ee7b7',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13,
          }}>
            <b style={{ color: 'var(--success)' }}>Student Submission:</b>
            <a href={item.submissionUrl} target="_blank" rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--primary)', marginTop: 4, wordBreak: 'break-all' }}>
              <FiExternalLink size={13} /> {item.submissionUrl}
            </a>
          </div>
        )}
        <div className="form-group">
          <label>Marks / {item.maxMarks || '?'}</label>
          <input type="number" value={marks} onChange={e => setMarks(e.target.value)}
            className="form-control" placeholder="Enter marks" />
        </div>
        <div className="form-group">
          <label>Feedback (optional)</label>
          <textarea value={feedback} onChange={e => setFeedback(e.target.value)}
            className="form-control" rows={3} placeholder="Great work on…" />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-outline">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary">
            {saving ? 'Saving…' : <><FiAward size={14} /> Save Grade</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Enrollment Content Editor ─── */
const Editor = ({ enrollmentId, onBack }) => {
  const [enr,         setEnr]         = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [openMods,    setOpenMods]    = useState({});
  const [addMod,      setAddMod]      = useState(false);
  const [modTitle,    setModTitle]    = useState('');
  const [editModId,   setEditModId]   = useState(null);
  const [editModTxt,  setEditModTxt]  = useState('');
  const [addingItem,  setAddingItem]  = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [savingItem,  setSavingItem]  = useState(false);
  const [infoOpen,    setInfoOpen]    = useState(false);
  const [info,        setInfo]        = useState({});
  const [savingInfo,  setSavingInfo]  = useState(false);
  const [gradeTarget, setGradeTarget] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    lmsGetEnrollment(enrollmentId)
      .then(r => {
        const e = r.data.enrollment;
        setEnr(e);
        setInfo({
          batchName:      e.batchName      || '',
          batchStartDate: e.batchStartDate ? e.batchStartDate.split('T')[0] : '',
          batchEndDate:   e.batchEndDate   ? e.batchEndDate.split('T')[0]   : '',
          studentNote:    e.studentNote    || '',
          isActive:       e.isActive ?? true,
        });
        const o = {};
        (e.modules || []).forEach(m => { o[m._id] = true; });
        setOpenMods(o);
      })
      .catch(err => toast.error('Load failed: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <Spin />;
  if (!enr)    return null;

  const student    = enr.student || {};
  const course     = enr.course  || {};
  const totalItems = (enr.modules || []).reduce((s, m) => s + (m.items?.length || 0), 0);
  const needGrade  = (enr.modules || []).reduce((s, m) =>
    s + (m.items || []).filter(i => i.type === 'assignment' && i.submittedAt && i.marksObtained == null).length, 0);

  const doAddMod = async () => {
    if (!modTitle.trim()) return;
    try {
      await lmsAddModule(enrollmentId, { title: modTitle });
      toast.success('Module added!');
      setModTitle(''); setAddMod(false); reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const doRenameMod = async (moduleId) => {
    if (!editModTxt.trim()) return;
    try {
      await lmsUpdateModule(enrollmentId, moduleId, { title: editModTxt });
      toast.success('Renamed!'); setEditModId(null); reload();
    } catch { toast.error('Rename failed'); }
  };

  const doDeleteMod = async (moduleId) => {
    if (!window.confirm('Delete this module and all its content?')) return;
    try { await lmsDeleteModule(enrollmentId, moduleId); toast.success('Deleted'); reload(); }
    catch { toast.error('Delete failed'); }
  };

  const doAddItem = async (moduleId, form) => {
    setSavingItem(true);
    try {
      await lmsAddItem(enrollmentId, moduleId, form);
      toast.success('✅ Content added! Student can see it now.');
      setAddingItem(null); reload();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSavingItem(false); }
  };

  const doUpdateItem = async (moduleId, itemId, form) => {
    setSavingItem(true);
    try {
      await lmsUpdateItem(enrollmentId, moduleId, itemId, form);
      toast.success('Updated!'); setEditingItem(null); reload();
    } catch { toast.error('Update failed'); }
    finally { setSavingItem(false); }
  };

  const doToggleVisible = async (moduleId, item) => {
    try {
      await lmsUpdateItem(enrollmentId, moduleId, item._id, { ...item, isVisible: !item.isVisible });
      toast.success(item.isVisible ? 'Hidden from student' : '✅ Visible to student');
      reload();
    } catch { toast.error('Failed'); }
  };

  const doDeleteItem = async (moduleId, itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try { await lmsDeleteItem(enrollmentId, moduleId, itemId); toast.success('Deleted'); reload(); }
    catch { toast.error('Delete failed'); }
  };

  const doSaveInfo = async () => {
    setSavingInfo(true);
    try {
      await lmsUpdateEnrollmentInfo(enrollmentId, info);
      toast.success('Saved!'); setInfoOpen(false); reload();
    } catch { toast.error('Save failed'); }
    finally { setSavingInfo(false); }
  };

  return (
    <div>
      {gradeTarget && (
        <GradeModal
          item={gradeTarget.item} moduleId={gradeTarget.moduleId}
          enrollmentId={enrollmentId}
          onClose={() => setGradeTarget(null)} onDone={reload}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onBack} className="btn btn-outline btn-sm">
          <FiArrowLeft size={13} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{student.fullName}</div>
          <div style={{ fontSize: 13, color: 'var(--mid)' }}>{student.email} · {course.title}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {needGrade > 0 && (
            <span style={{ background: '#fef3c7', color: '#b45309', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700 }}>
              📝 {needGrade} need grading
            </span>
          )}
          <span className="badge badge-success">{totalItems} items · {enr.progressPercent || 0}% done</span>
        </div>
      </div>

      {/* Batch Info */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, fontSize: 14 }}>📋 Batch Info & Student Note</h4>
          <button onClick={() => setInfoOpen(o => !o)} className="btn btn-outline btn-sm">
            <FiEdit2 size={12} /> {infoOpen ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {infoOpen ? (
          <div style={{ marginTop: 16 }}>
            <div className="form-grid">
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Batch Name</label>
                <input value={info.batchName}
                  onChange={e => setInfo(f => ({ ...f, batchName: e.target.value }))}
                  className="form-control" placeholder="Batch Jan 2025" />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Visibility</label>
                <select value={info.isActive ? 'true' : 'false'}
                  onChange={e => setInfo(f => ({ ...f, isActive: e.target.value === 'true' }))}
                  className="form-control">
                  <option value="true">Active — visible to student</option>
                  <option value="false">Inactive — hidden from student</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>Start Date</label>
                <input type="date" value={info.batchStartDate}
                  onChange={e => setInfo(f => ({ ...f, batchStartDate: e.target.value }))}
                  className="form-control" />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>End Date</label>
                <input type="date" value={info.batchEndDate}
                  onChange={e => setInfo(f => ({ ...f, batchEndDate: e.target.value }))}
                  className="form-control" />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Note to Student <span style={{ color: 'var(--mid)', fontSize: 12, fontWeight: 400 }}>(shown on their dashboard)</span></label>
              <textarea value={info.studentNote}
                onChange={e => setInfo(f => ({ ...f, studentNote: e.target.value }))}
                className="form-control" rows={2}
                placeholder="Welcome! Please complete Week 1 by Friday…" />
            </div>
            <button onClick={doSaveInfo} disabled={savingInfo} className="btn btn-primary btn-sm">
              {savingInfo ? 'Saving…' : <><FiSave size={13} /> Save Info</>}
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13, color: 'var(--mid)' }}>
            {info.batchName && <span>📅 <b>{info.batchName}</b></span>}
            {info.batchStartDate && <span>Start: {new Date(info.batchStartDate).toLocaleDateString('en-IN')}</span>}
            {info.batchEndDate   && <span>End: {new Date(info.batchEndDate).toLocaleDateString('en-IN')}</span>}
            {info.studentNote    && <span style={{ color: '#92400e' }}>📌 "{info.studentNote.slice(0, 80)}{info.studentNote.length > 80 ? '…' : ''}"</span>}
            {!info.batchName && !info.batchStartDate && !info.studentNote && <span>No info set. Click Edit.</span>}
          </div>
        )}
      </div>

      {/* Modules */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 15 }}>📚 Course Modules</h4>
        <button onClick={() => setAddMod(true)} className="btn btn-primary btn-sm">
          <FiPlus size={13} /> Add Module
        </button>
      </div>

      {addMod && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, background: 'white', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <input value={modTitle} onChange={e => setModTitle(e.target.value)}
            className="form-control" placeholder="e.g. Week 1 — HTML & CSS Basics"
            onKeyDown={e => e.key === 'Enter' && doAddMod()} autoFocus />
          <button onClick={doAddMod} className="btn btn-primary btn-sm"><FiCheck size={13} /></button>
          <button onClick={() => { setAddMod(false); setModTitle(''); }} className="btn btn-outline btn-sm"><FiX size={13} /></button>
        </div>
      )}

      {(enr.modules || []).length === 0 && !addMod && (
        <div style={{ textAlign: 'center', padding: 40, background: 'white', border: '2px dashed var(--border)', borderRadius: 16, color: 'var(--mid)' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📭</div>
          <p style={{ marginBottom: 16 }}>No modules yet. Add a module first, then add content inside it.</p>
          <button onClick={() => setAddMod(true)} className="btn btn-primary">
            <FiPlus size={14} /> Add First Module
          </button>
        </div>
      )}

      {(enr.modules || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map(mod => (
        <div key={mod._id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg)', borderBottom: openMods[mod._id] ? '1px solid var(--border)' : 'none' }}>
            <button onClick={() => setOpenMods(o => ({ ...o, [mod._id]: !o[mod._id] }))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mid)', display: 'flex', padding: 0 }}>
              {openMods[mod._id] ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </button>
            {editModId === mod._id ? (
              <>
                <input value={editModTxt} onChange={e => setEditModTxt(e.target.value)}
                  className="form-control" style={{ flex: 1, padding: '6px 10px' }} autoFocus
                  onKeyDown={e => e.key === 'Enter' && doRenameMod(mod._id)} />
                <button onClick={() => doRenameMod(mod._id)} className="btn btn-primary btn-sm"><FiCheck size={12} /></button>
                <button onClick={() => setEditModId(null)} className="btn btn-outline btn-sm"><FiX size={12} /></button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 14 }}>{mod.title}</span>
                <span style={{ fontSize: 12, color: 'var(--mid)' }}>{(mod.items || []).length} items</span>
                <button onClick={() => { setEditModId(mod._id); setEditModTxt(mod.title); }} className="btn btn-outline btn-sm"><FiEdit2 size={12} /></button>
                <button onClick={() => doDeleteMod(mod._id)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}><FiTrash2 size={12} /></button>
              </>
            )}
          </div>

          {openMods[mod._id] && (
            <div style={{ padding: '8px 16px 14px' }}>
              {(mod.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map(item => {
                const isEditing  = editingItem?.moduleId === mod._id && editingItem?.item?._id === item._id;
                const needsGrade = item.type === 'assignment' && item.submittedAt && item.marksObtained == null;
                return (
                  <div key={item._id} style={{ marginBottom: 8 }}>
                    {isEditing ? (
                      <ItemForm initial={item}
                        onSave={form => doUpdateItem(mod._id, item._id, form)}
                        onCancel={() => setEditingItem(null)} saving={savingItem} />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, background: item.isVisible ? 'white' : '#f8fafc', opacity: item.isVisible ? 1 : 0.7 }}>
                        <TypeChip type={item.type} />
                        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: item.isVisible ? 'var(--dark)' : 'var(--mid)' }}>
                          {item.title}
                          {!item.isVisible && <span style={{ fontSize: 11, color: 'var(--mid)', marginLeft: 8 }}>(hidden from student)</span>}
                        </span>
                        {needsGrade && <span style={{ background: '#fef3c7', color: '#b45309', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>⚠ Grade Me</span>}
                        {item.marksObtained != null && <span style={{ background: '#d1fae5', color: 'var(--success)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 }}>✅ {item.marksObtained}/{item.maxMarks}</span>}
                        {item.type === 'assignment' && item.submittedAt && (
                          <button onClick={() => setGradeTarget({ moduleId: mod._id, item })}
                            className="btn btn-sm"
                            style={{ background: needsGrade ? '#fef3c7' : '#f0f4ff', color: needsGrade ? '#b45309' : 'var(--primary)', fontSize: 12 }}>
                            {item.marksObtained != null ? '✏️ Re-grade' : '📊 Grade'}
                          </button>
                        )}
                        <button onClick={() => doToggleVisible(mod._id, item)} className="btn btn-outline btn-sm" title={item.isVisible ? 'Hide' : 'Show'}>
                          {item.isVisible ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                        </button>
                        <button onClick={() => setEditingItem({ moduleId: mod._id, item })} className="btn btn-outline btn-sm"><FiEdit2 size={12} /></button>
                        <button onClick={() => doDeleteItem(mod._id, item._id)} className="btn btn-sm" style={{ background: '#fee2e2', color: 'var(--error)' }}><FiTrash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                );
              })}

              {addingItem === mod._id ? (
                <ItemForm
                  onSave={form => doAddItem(mod._id, form)}
                  onCancel={() => setAddingItem(null)} saving={savingItem} />
              ) : (
                <button onClick={() => setAddingItem(mod._id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: '1.5px dashed var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 13, fontWeight: 600, marginTop: 6, width: '100%', transition: '0.2s' }}
                  onMouseOver={e => { e.currentTarget.style.background = 'var(--primary-light)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onMouseOut={e  => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                  <FiPlus size={14} /> Add Video / File / Assignment / Note / Link
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
export default function AdminLmsPage() {
  const [view,           setView]           = useState('all'); // ← starts on ALL ENROLLMENTS now
  const [query,          setQuery]          = useState('');
  const [results,        setResults]        = useState([]);
  const [searching,      setSearching]      = useState(false);
  const [searchErr,      setSearchErr]      = useState('');
  const [selStudent,     setSelStudent]     = useState(null);
  const [stuEnrollments, setStuEnrollments] = useState([]);
  const [loadingStu,     setLoadingStu]     = useState(false);
  const [courses,        setCourses]        = useState([]);
  const [courseId,       setCourseId]       = useState('');
  const [enrolling,      setEnrolling]      = useState(false);
  const [allEnrollments, setAllEnrollments] = useState([]);
  const [loadingAll,     setLoadingAll]     = useState(false);
  const [allErr,         setAllErr]         = useState('');
  const [activeId,       setActiveId]       = useState(null);
  const timerRef = useRef(null);

  // Load courses + all enrollments on mount
  useEffect(() => {
    getAdminCourses().then(r => setCourses(r.data.courses || [])).catch(() => {});
    loadAll();
  }, []);

  const loadAll = () => {
    setLoadingAll(true);
    setAllErr('');
    lmsGetAllEnrollments({})
      .then(r => setAllEnrollments(r.data.enrollments || []))
      .catch(err => {
        const msg = err.response?.data?.message || err.message || 'Unknown error';
        setAllErr(msg);
        toast.error('Could not load enrollments: ' + msg);
      })
      .finally(() => setLoadingAll(false));
  };

  const search = (q) => {
    clearTimeout(timerRef.current);
    setQuery(q);
    setSearchErr('');
    if (!q.trim()) { setResults([]); return; }
    timerRef.current = setTimeout(() => {
      setSearching(true);
      lmsSearchStudents(q)
        .then(r => {
          const list = r.data.students || [];
          setResults(list);
          if (list.length === 0) setSearchErr('No users found for "' + q + '". Make sure the student has registered an account first.');
        })
        .catch(err => {
          setResults([]);
          setSearchErr('Search failed: ' + (err.response?.data?.message || err.message));
        })
        .finally(() => setSearching(false));
    }, 400);
  };

  const pickStudent = (s) => {
    setSelStudent(s);
    setResults([]);
    setQuery(s.fullName + ' (' + s.email + ')');
    setSearchErr('');
    loadStuEnrollments(s._id);
  };

  const loadStuEnrollments = (studentId) => {
    setLoadingStu(true);
    lmsGetStudentEnrollments(studentId)
      .then(r => setStuEnrollments(r.data.enrollments || []))
      .catch(err => toast.error('Could not load: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoadingStu(false));
  };

  const enroll = async () => {
    if (!selStudent || !courseId) { toast.error('Pick a student and a course'); return; }
    setEnrolling(true);
    try {
      await lmsEnrollStudent({ studentId: selStudent._id, courseId });
      toast.success(selStudent.fullName + ' enrolled! ✅');
      setCourseId('');
      loadStuEnrollments(selStudent._id);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Enrollment failed');
    } finally { setEnrolling(false); }
  };

  if (activeId) {
    return (
      <div style={{ padding: 24 }}>
        <Editor enrollmentId={activeId} onBack={() => { setActiveId(null); loadAll(); }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <style>{`
        .lt { display:flex; gap:4; background:white; border:1px solid var(--border); border-radius:10px; padding:5px; width:fit-content; margin-bottom:24px; }
        .lt-btn { padding:8px 20px; border-radius:8px; font-size:14px; font-weight:600; cursor:pointer; border:none; font-family:var(--font-display); display:flex; align-items:center; gap:7px; transition:0.2s; }
        .lt-btn.on { background:var(--primary); color:white; }
        .lt-btn:not(.on) { background:none; color:var(--mid); }
        .lt-btn:not(.on):hover { background:var(--bg); color:var(--dark); }
        .sr { display:flex; align-items:center; gap:12px; padding:12px 16px; cursor:pointer; border-bottom:1px solid var(--border); transition:0.15s; }
        .sr:hover { background:var(--primary-light); }
        .sa { width:38px; height:38px; border-radius:50%; background:linear-gradient(135deg,var(--primary),var(--accent)); display:flex; align-items:center; justify-content:center; color:white; font-weight:700; font-size:16px; flex-shrink:0; }
        .er { background:white; border:1px solid var(--border); border-radius:10px; padding:16px 20px; margin-bottom:10px; display:flex; align-items:center; gap:14px; transition:0.2s; }
        .er:hover { border-color:var(--primary); box-shadow:var(--shadow-sm); }
      `}</style>

      {/* Tabs */}
      <div className="lt">
        <button className={`lt-btn${view === 'all' ? ' on' : ''}`} onClick={() => { setView('all'); loadAll(); }}>
          <FiList size={14} /> All Enrollments
        </button>
        <button className={`lt-btn${view === 'search' ? ' on' : ''}`} onClick={() => setView('search')}>
          <FiSearch size={14} /> Find Student
        </button>
      </div>

      {/* ══ ALL ENROLLMENTS ══ */}
      {view === 'all' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 14, color: 'var(--mid)' }}>
              {loadingAll ? 'Loading…' : `${allEnrollments.length} enrollment${allEnrollments.length !== 1 ? 's' : ''} found`}
            </div>
            <button onClick={loadAll} className="btn btn-outline btn-sm">
              <FiRefreshCw size={12} /> Refresh
            </button>
          </div>

          {allErr && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '14px 18px', marginBottom: 16, fontSize: 14 }}>
              ❌ Error loading enrollments: <b>{allErr}</b>
              <br /><span style={{ fontSize: 12, color: '#991b1b' }}>Make sure your backend is running and <code>backend/routes/lms.js</code> is the new version.</span>
            </div>
          )}

          {loadingAll ? <Spin /> : allEnrollments.length === 0 && !allErr ? (
            <div style={{ textAlign: 'center', padding: 48, background: 'white', border: '1px dashed var(--border)', borderRadius: 16, color: 'var(--mid)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <h3 style={{ marginBottom: 8, color: 'var(--dark)' }}>No Enrollments Yet</h3>
              <p style={{ marginBottom: 20, maxWidth: 380, margin: '0 auto 20px', lineHeight: 1.7 }}>
                Use <b>Find Student</b> tab to search for a student and enroll them in a course.
              </p>
              <button onClick={() => setView('search')} className="btn btn-primary">
                <FiSearch size={14} /> Find Student
              </button>
            </div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', fontSize: 12, color: 'var(--mid)', textTransform: 'uppercase' }}>
                    {['Student', 'Course', 'Batch', 'Modules', 'Progress', 'Enrolled', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allEnrollments.map(enr => {
                    const pending = (enr.modules || []).reduce((s, m) =>
                      s + (m.items || []).filter(i => i.type === 'assignment' && i.submittedAt && i.marksObtained == null).length, 0);
                    return (
                      <tr key={enr._id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600 }}>{enr.student?.fullName}</div>
                          <div style={{ fontSize: 12, color: 'var(--mid)' }}>{enr.student?.email}</div>
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--mid)' }}>{enr.course?.title}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--mid)' }}>{enr.batchName || '—'}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--mid)', textAlign: 'center' }}>{enr.modules?.length || 0}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ height: 6, width: 60, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{ height: '100%', background: 'var(--primary)', width: `${enr.progressPercent || 0}%`, borderRadius: 99 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{enr.progressPercent || 0}%</span>
                          </div>
                          {pending > 0 && (
                            <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700, marginTop: 3 }}>
                              📝 {pending} to grade
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--mid)', whiteSpace: 'nowrap' }}>
                          {enr.createdAt ? new Date(enr.createdAt).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <button onClick={() => setActiveId(enr._id)} className="btn btn-primary btn-sm">
                            <FiEdit2 size={12} /> Manage
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ FIND STUDENT ══ */}
      {view === 'search' && (
        <div style={{ maxWidth: 760 }}>

          {/* Search box */}
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--mid)' }} />
            <input
              value={query}
              onChange={e => search(e.target.value)}
              className="form-control"
              style={{ paddingLeft: 42, fontSize: 15 }}
              placeholder="Type student name or email…"
            />
            {searching && (
              <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }} />
            )}
          </div>

          {/* Search error */}
          {searchErr && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 14, fontSize: 13 }}>
              ⚠️ {searchErr}
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 20, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
              {results.map(s => (
                <div key={s._id} className="sr" onClick={() => pickStudent(s)}>
                  <div className="sa">{s.fullName ? s.fullName[0].toUpperCase() : '?'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{s.fullName}</div>
                    <div style={{ fontSize: 12, color: 'var(--mid)' }}>
                      {s.email}
                      {s.role === 'admin' && (
                        <span style={{ marginLeft: 8, background: '#fee2e2', color: 'var(--error)', fontSize: 11, padding: '1px 6px', borderRadius: 4 }}>Admin</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>Select →</span>
                </div>
              ))}
            </div>
          )}

          {/* Selected student */}
          {selStudent && (
            <>
              <div style={{ background: 'linear-gradient(135deg,#f0f4ff,white)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                  <div className="sa" style={{ width: 52, height: 52, fontSize: 22 }}>
                    {selStudent.fullName ? selStudent.fullName[0].toUpperCase() : '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 17 }}>{selStudent.fullName}</div>
                    <div style={{ fontSize: 13, color: 'var(--mid)' }}>{selStudent.email}</div>
                  </div>
                  <button onClick={() => { setSelStudent(null); setQuery(''); setStuEnrollments([]); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--mid)' }}>
                    <FiX />
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Enroll in Course</label>
                    <select value={courseId} onChange={e => setCourseId(e.target.value)} className="form-control">
                      <option value="">— Select a course —</option>
                      {courses.map(c => {
                        const already = stuEnrollments.some(e =>
                          (e.course?._id || e.course)?.toString() === c._id?.toString()
                        );
                        return (
                          <option key={c._id} value={c._id} disabled={already}>
                            {c.title}{already ? ' ✓ enrolled' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <button onClick={enroll} disabled={enrolling || !courseId} className="btn btn-primary">
                    {enrolling ? 'Enrolling…' : <><FiUserPlus size={14} /> Enroll</>}
                  </button>
                  <button onClick={() => loadStuEnrollments(selStudent._id)} className="btn btn-outline btn-sm">
                    <FiRefreshCw size={13} />
                  </button>
                </div>
              </div>

              <h4 style={{ marginBottom: 12, fontSize: 15 }}>
                📚 {selStudent.fullName.split(' ')[0]}'s Enrolled Courses
              </h4>

              {loadingStu ? <Spin /> : stuEnrollments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, background: 'white', border: '2px dashed var(--border)', borderRadius: 12, color: 'var(--mid)' }}>
                  No enrollments for this student yet. Select a course above and click Enroll.
                </div>
              ) : stuEnrollments.map(enr => {
                const c     = enr.course || {};
                const total = (enr.modules || []).reduce((s, m) => s + (m.items?.length || 0), 0);
                const grades = (enr.modules || []).reduce((s, m) =>
                  s + (m.items || []).filter(i => i.type === 'assignment' && i.submittedAt && i.marksObtained == null).length, 0);
                return (
                  <div key={enr._id} className="er">
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                      {c.category === 'Development' ? '💻' : c.category === 'Data Science' ? '🤖' : '📱'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{c.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--mid)' }}>
                        {(enr.modules || []).length} modules · {total} items{enr.batchName ? ` · 📅 ${enr.batchName}` : ''}
                      </div>
                    </div>
                    <div style={{ width: 80, flexShrink: 0 }}>
                      <div style={{ height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', marginBottom: 3 }}>
                        <div style={{ height: '100%', background: 'var(--primary)', width: `${enr.progressPercent || 0}%`, borderRadius: 99 }} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--mid)', textAlign: 'center' }}>{enr.progressPercent || 0}%</div>
                    </div>
                    {grades > 0 && (
                      <span style={{ background: '#fef3c7', color: '#b45309', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, flexShrink: 0 }}>
                        📝 {grades} to grade
                      </span>
                    )}
                    <button onClick={() => setActiveId(enr._id)} className="btn btn-primary btn-sm">
                      <FiEdit2 size={13} /> Manage
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {!selStudent && !query && (
            <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--mid)' }}>
              <div style={{ fontSize: 56, marginBottom: 14 }}>🔍</div>
              <h3 style={{ marginBottom: 8, color: 'var(--dark)' }}>Find a Student</h3>
              <p style={{ maxWidth: 360, margin: '0 auto', fontSize: 14, lineHeight: 1.7 }}>
                Type a student name or email. They must have registered an account first.
                Once found, enroll them in a course and add content.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
